// @noflow
import { useLocalisation } from '@/context/localisation/localisation'
import { useLazyQuery, useQuery } from '@apollo/client'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import { addDays, subDays } from 'date-fns'
import isNull from 'lodash/isNull'
import isUndefined from 'lodash/isUndefined'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { countryCodeToLocaleCurrency } from '@/utils/countryCodeHelper'
import { formatCurrencyWithDecimal } from '@/utils/currency'
import scrollToElement from '@/utils/scrollToElement'
import * as Sentry from '@/utils/sentry'

import BREAKPOINTS from '@/constants/Breakpoints'

import useSubscriptionResume from '@/hooks/subscriptionResume'
import useWindowSize from '@/hooks/useWindowSize'

import BoxIcon from 'assets/images/icons/box.svg'
import ButternutBoxSprite from 'assets/images/illustrations/loading-screens/meal-pouch-with-bowl-and-yellow-hearts-illustration--sprite.svg'

import segmentTrack from '@/components/analytics/Analytics'
import { Button } from '@/components/elements/atoms/Button'
import Notification from '@/components/elements/atoms/InPageNotification/InPageNotification'
import { SectionWrapper } from '@/components/elements/atoms/SectionWrapper'
import Separator from '@/components/elements/atoms/Separator/Separator'
import Text from '@/components/elements/atoms/Text/Text'
import { MissingPaymentAlert } from '@/components/elements/molecules/MissingPaymentAlert'
import NotificationContent, {
  V2Variant
} from '@/components/elements/molecules/NotificationContent/NotificationContent'
import { OOBCard } from '@/components/elements/molecules/OOBCard/OOBCard'
import { ErrorState } from '@/components/elements/organisms/ErrorState'
import LoadingScreen from '@/components/elements/organisms/LoadingScreen/LoadingScreen'
import SuccessOverlay from '@/components/elements/organisms/SuccessOverlay/SuccessOverlay'
import UpcomingBoxes from '@/components/pages/Dashboard/components/upcomingBoxes/UpcomingBoxes'
import { upcomingBoxQuery_user_ordersByDate_Box as Box } from '@/components/pages/Dashboard/components/upcomingBoxes/queries/__generated__/upcomingBoxQuery'
import STYLES from '@/components/pages/SegmentedPausedPage/SegmentedPausedPage.module.sass'
import {
  TreatmentsPausedFutureBoxesQuery_user_subscription_previewUncreatedFutureBox as FutureBox,
  TreatmentsPausedFutureBoxesQuery
} from '@/components/pages/TreatmentsPausedPage/queries/__generated__/TreatmentsPausedFutureBoxesQuery'
import { TREATMENTS_PAUSED_PAGE_FUTURE_BOXES_QUERY } from '@/components/pages/TreatmentsPausedPage/queries/queries'
import Base from '@/components/templates/Base'

import { SEGMENTED_PAUSED_PAGE_QUERY } from './queries/queries'

import { EditPlan, PlanSummary } from './components'

const NOW = new Date()

const SegmentedPausedPage = (): JSX.Element | null => {
  const namespace = 'account'
  const { format } = useLocalisation()
  const { t } = useTranslation('account')
  const { windowWidth } = useWindowSize()
  const isMobile = windowWidth < BREAKPOINTS.md
  const [resumeLoading, setResumeLoading] = useState(false)
  const urlParams = new URLSearchParams(window.location.search)
  const showOOBSuccess = urlParams.get('success') === 'orderedOOB'
  const [pausedDeliveryDate, setPausedDeliveryDate] = useState<Date>()

  const [uncreatedBox, setUncreatedBox] = useState<FutureBox | null>(null)

  const [getFutureBoxes, { loading: lazyFutureBoxesLoading }] =
    useLazyQuery<TreatmentsPausedFutureBoxesQuery>(
      TREATMENTS_PAUSED_PAGE_FUTURE_BOXES_QUERY,
      {
        onCompleted(futureBoxes) {
          setUncreatedBox(
            futureBoxes.user.subscription.previewUncreatedFutureBox
          )
        }
      }
    )

  const { data, error, loading } = useQuery(SEGMENTED_PAUSED_PAGE_QUERY, {
    variables: {
      from: subDays(NOW, 3),
      to: addDays(NOW, 60)
    },
    onError: (error) =>
      Sentry.captureException('Error occurred in SEGMENTED_PAUSED_PAGE_QUERY', {
        extra: { error },
        tags: {
          product: Sentry.Product.Account,
          team: Sentry.Team.Retention
        }
      })
  })

  useEffect((): void => {
    // Only retrieve `previewUncreatedFutureBox` if the user has a deliverable address
    if (data?.user?.address?.deliverable) getFutureBoxes()
  }, [data?.user?.address?.deliverable, getFutureBoxes, loading])

  const { handleResumeSubscription } = useSubscriptionResume({
    hideToast: true
  })

  useEffect((): void => {
    const date = uncreatedBox?.isoDeliveryDate
      ? new Date(uncreatedBox?.isoDeliveryDate)
      : NOW

    if (uncreatedBox) setPausedDeliveryDate(date)
  }, [uncreatedBox, lazyFutureBoxesLoading])

  const navigateToAccount = useCallback(() => {
    setTimeout((): void => {
      window.location.href = '/account?topCard=welcomeBack'
    }, 1000)
  }, [])

  const scrollToOrderViewer = useCallback(() => {
    const el = document.getElementById('order-carousel')
    if (el) scrollToElement(el as HTMLElement)
  }, [])

  const triggerError = useCallback((error, msg) => {
    Sentry.captureException(msg, {
      extra: { error },
      tags: {
        product: Sentry.Product.Account,
        team: Sentry.Team.Retention
      }
    })

    toast.error(
      <NotificationContent
        copy={{
          text: 'notification.resume_failed',
          namespace: 'limited_offer_paused_page'
        }}
      />,
      V2Variant.error()
    )
  }, [])

  const restartPreviousPlanClicked = useCallback(async (): Promise<void> => {
    segmentTrack('Segmented Paused Page - Restart Previous Plan clicked')
    setResumeLoading(true)

    try {
      await handleResumeSubscription({
        variables: {
          deliveryDate: pausedDeliveryDate
        }
      })
    } catch (error) {
      triggerError(
        error,
        'Could not resume subscription from SUBSCRIPTION_RESUME_MUTATION'
      )
    } finally {
      setResumeLoading(false)
      navigateToAccount()
    }
  }, [
    handleResumeSubscription,
    pausedDeliveryDate,
    triggerError,
    navigateToAccount
  ])

  if (error) {
    return (
      <ErrorState
        error={{
          name: 'Error returning Segmented Paused Page',
          message: error.message,
          apollo: error
        }}
      />
    )
  }

  if (loading || !data) {
    return (
      <LoadingScreen
        isOpen
        title={{
          text: 'loading_screen.title',
          namespace: 'shared'
        }}
      />
    )
  }

  const {
    user: {
      ordersByDate,
      preferredLanguage,
      shippingCountryCode,
      subscription: {
        status,
        currentPause,
        previewUncreatedFutureBox,
        activePaymentMethodType
      }
    }
  } = data

  const { plan, order } = previewUncreatedFutureBox || {}
  const { deliveryFee, subtotal } = order || {}

  // Get locale and currency and format pricings
  const { locale, currency } = countryCodeToLocaleCurrency(
    shippingCountryCode,
    preferredLanguage
  )

  const dailyPriceFormatted = formatCurrencyWithDecimal(plan.pricePerDay, {
    locale: locale,
    currency: currency
  })

  const OOBPriceFormatted = formatCurrencyWithDecimal(subtotal + deliveryFee, {
    locale: locale,
    currency: currency
  })

  // Check if oob cards are available and add conditions for plan edit
  const OOBCardAvailable =
    currentPause?.segmentedTreatments.includes('OneOffBox')
  const ResumeCardAvailable =
    currentPause?.segmentedTreatments.includes('ResumeCard')
  const EditPlanCardAvailable =
    currentPause?.segmentedTreatments.includes('EditPlan')
  // OneOffBox ordered
  const upcomingOneOffBox = ordersByDate.find(
    (order: Box) =>
      'id' in order && order.isOneOffBox === true && order.descriptor !== 'past'
  )

  // Success screen for restarting previous plan
  if (status === 'active' && !resumeLoading) {
    return (
      <SuccessOverlay
        sprite={ButternutBoxSprite}
        dataTestId="subscription-confirmation-overlay"
        typography={{
          namespace: 'treatments_paused_page',
          text: 'success',
          margin: false
        }}
      />
    )
  }

  const formattedDate =
    !isUndefined(format) && pausedDeliveryDate && !lazyFutureBoxesLoading
      ? format(pausedDeliveryDate, 'iii do MMM')
      : ''

  return (
    <Base background="brandYellow100">
      <Container maxWidth={false} disableGutters className={STYLES.container}>
        <div className={STYLES.content}>
          {isNull(activePaymentMethodType) && <MissingPaymentAlert />}
          {showOOBSuccess && upcomingOneOffBox && (
            <div className={STYLES.highlightedSection}>
              <Notification
                text={{
                  text: 'price_sensitive_paused_page.oob_success_alert.body',
                  namespace,
                  variables: {
                    deliveryDate: !isUndefined(format)
                      ? format(upcomingOneOffBox.isoDeliveryDate, 'iii do MMM')
                      : ''
                  }
                }}
                variant="success"
              >
                <Button
                  onClick={scrollToOrderViewer}
                  typography={{
                    namespace,
                    text: `price_sensitive_paused_page.oob_success_alert.link`,
                    bold: true,
                    colour: 'supportGreen500',
                    variant: 'textRegular16',
                    shouldScale: true
                  }}
                  variant="noStyling"
                  identifier="coming_back_paused_page.oob_success_alert.link"
                />
              </Notification>
            </div>
          )}
          <div className={STYLES.headerWrapper}>
            <img
              alt={t(
                'segmented_paused_page.coming_back_segment.header_icon_alt'
              )}
              src={BoxIcon}
              width={64}
              height={57}
            />
            <Text
              namespace={namespace}
              text={`segmented_paused_page.coming_back_segment.header`}
              element="h1"
              variant="display24"
              margin={false}
            />
          </div>
          <Grid
            container
            justifyContent="flex-start"
            className={STYLES.topCards}
            spacing={isMobile ? 0 : 4}
          >
            {ResumeCardAvailable && (
              <Grid item xs={12} md={6}>
                <PlanSummary
                  deliveryDate={formattedDate}
                  lazyFutureBoxesLoading={lazyFutureBoxesLoading}
                  deliveryFrequency={plan.durationInWeeks}
                  dailyAmount={plan.pouchesPerDay * plan.pouchSize}
                  dailyPrice={dailyPriceFormatted}
                  onClick={restartPreviousPlanClicked}
                  disabled={resumeLoading || isNull(activePaymentMethodType)}
                />
              </Grid>
            )}
            {EditPlanCardAvailable && (
              <Grid item xs={12} md={6}>
                <EditPlan
                  identifier="coming_back_paused_page.edit_your_plan_cta"
                  disableBtn={isNull(activePaymentMethodType)}
                />
              </Grid>
            )}
          </Grid>
          {OOBCardAvailable && (
            <OOBCard
              OOBPriceFormatted={OOBPriceFormatted}
              variant="topImage"
              identifier="coming_back_paused_page.oob_card.review_oob_cta"
            />
          )}
          {ordersByDate.length > 0 && (
            <>
              <div className={STYLES.separator}>
                <Separator handdrawn />
              </div>
              <SectionWrapper
                margin={0}
                headerTypography={{
                  text: 'upcoming_boxes.title',
                  namespace: 'treatments_paused_page'
                }}
              >
                <UpcomingBoxes filter={['past']} />
              </SectionWrapper>
            </>
          )}
        </div>
      </Container>
    </Base>
  )
}

export default SegmentedPausedPage
